import React, { useCallback } from 'react';
import { PiTruck, PiUser, PiBuilding } from 'react-icons/pi';
import { useParams } from 'react-router-dom';
import { Input, Textarea } from 'rizzui';
import useFetch from '../../../../../hooks/useFetch';
import FormGroup from '../../../../../layout/shared/form-group';
import moment from 'moment';
import { PickupService } from '../../../../../services/pickup/pickupService';
import { PickupApiResponse } from '../../../../../type/entities/pickup-type';
import { pickupStatuses } from '../../../../../utils/SelectorMapping';
import CustomSelect from '../../../../../components/forms/CustomSelect';

interface PickupInfoProps { }

const PickupInfo: React.FC<PickupInfoProps> = () => {

    // HOOKS
    
    const { id = '' } = useParams<{ id: string }>();
    const service = new PickupService();

    // DATA FETCHING

    // -----------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE LOS DATOS DE LA RECOGIDA POR ID
     * @EN GETS THE PICKUP DATA BY ID
     */
    // -----------------------------------------------------------------------------------------------------
    const [data, loading] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getPickupById(id);
        return response.getResponseData() as PickupApiResponse;    
    }, [id]));
    // -----------------------------------------------------------------------------------------------------

    // RENDER

    return (
        <>
            <div className="@container">
                <div className="grid gap-7 divide-y-2 divide-dashed divide-gray-200">
                    <FormGroup
                        title="Información General"
                        description="Datos principales de la recogida"
                        className='pt-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-4"
                    >
                        <Input
                            disabled
                            label="Fecha de Recogida"
                            className="md:col-span-1"
                            value={data?.pickupDate ? moment(data.pickupDate.date).format('DD/MM/YYYY HH:mm') : ""}
                        />
                        <Input
                            disabled
                            label="Fecha de Firma"
                            className="md:col-span-1"
                            value={data?.signedAt ? moment(data.signedAt.date).format('DD/MM/YYYY HH:mm') : 'No firmado'}
                        />
                        <CustomSelect
                            isDisabled
                            id="pickupStatus"
                            options={pickupStatuses}
                            label="Estado"
                            className="md:col-span-1"
                            value={
                                {
                                    label: pickupStatuses.find((item) => item.value === data?.pickupStatus)?.label ?? "",
                                    value: data?.pickupStatus ?? ""
                                }
                            }
                        />
                        <Input
                            disabled
                            label="Peso Recogido (kg)"
                            className="md:col-span-1"
                            value={data?.pickedWeightKg ?? ""}
                        />
                        <Input
                            disabled
                            label="Número de Recibo"
                            className="md:col-span-2"
                            value={data?.receiptNumber ?? ""}
                        />
                        <Input
                            disabled
                            label="Firmado"
                            className="md:col-span-1"
                            value={data?.isSigned ? 'Sí' : 'No'}
                        />
                        <Textarea
                            disabled
                            label="Comentarios"
                            className="md:col-span-4"
                            value={data?.comments ?? ""}
                            rows={3}
                        />
                        <Input
                            disabled
                            label="Fecha de Creación"
                            className="md:col-span-2"
                            value={data?.createdAt ? moment(data.createdAt.date).format('DD/MM/YYYY HH:mm') : ""}
                        />
                        <Input
                            disabled
                            label="Fecha de Actualización"
                            className="md:col-span-2"
                            value={data?.updatedAt ? moment(data.updatedAt.date).format('DD/MM/YYYY HH:mm') : ""}
                        />
                    </FormGroup>

                    <FormGroup
                        title="Sede"
                        description="Información de la sede de recogida"
                        className='pt-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-3"
                    >
                        <Input
                            disabled
                            prefix={<PiBuilding className="h-6 w-6 text-gray-500" />}
                            label="Nombre de la Sede"
                            className="md:col-span-1"
                            value={data?.headquarters?.name ?? ""}
                        />
                        <Input
                            disabled
                            label="Dirección"
                            className="md:col-span-1"
                            value={data?.headquarters?.address ?? ""}
                        />
                        <Input
                            disabled
                            label="Ciudad"
                            className="md:col-span-1"
                            value={data?.headquarters?.city ?? ""}
                        />
                    </FormGroup>

                    <FormGroup
                        title="Usuario Asignado"
                        description="Información del conductor asignado"
                        className='pt-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-3"
                    >
                        <Input
                            disabled
                            prefix={<PiUser className="h-6 w-6 text-gray-500" />}
                            label="Nombre"
                            className="md:col-span-1"
                            value={data?.user?.name ?? ""}
                        />
                        <Input
                            disabled
                            label="Apellidos"
                            className="md:col-span-1"
                            value={data?.user?.lastName ?? ""}
                        />
                        <Input
                            disabled
                            label="Email"
                            className="md:col-span-1"
                            value={data?.user?.email ?? ""}
                        />
                    </FormGroup>

                    <FormGroup
                        title="Vehículo"
                        description="Información del vehículo utilizado"
                        className='pt-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-3"
                    >
                        <Input
                            disabled
                            prefix={<PiTruck className="h-6 w-6 text-gray-500" />}
                            label="Matrícula"
                            className="md:col-span-1"
                            value={data?.vehicle?.plateNumber ?? ""}
                        />
                        <Input
                            disabled
                            label="Marca"
                            className="md:col-span-1"
                            value={data?.vehicle?.brand ?? ""}
                        />
                        <Input
                            disabled
                            label="Modelo"
                            className="md:col-span-1"
                            value={data?.vehicle?.model ?? ""}
                        />
                    </FormGroup>

                    <FormGroup
                        title="Ruta"
                        description="Información de la ruta asignada"
                        className='pt-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-2"
                    >
                        <Input
                            disabled
                            label="Nombre de la Ruta"
                            className="md:col-span-1"
                            value={data?.route?.name ?? ""}
                        />
                        <Input
                            disabled
                            label="Descripción"
                            className="md:col-span-1"
                            value={data?.route?.description ?? ""}
                        />
                    </FormGroup>

                    <FormGroup
                        title="Organización"
                        description="Datos de la organización"
                        className='pt-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-3"
                    >
                        <Input
                            disabled
                            label="Nombre"
                            value={data?.company?.name ?? ""}
                        />
                        <Input
                            disabled
                            label="CIF"
                            value={data?.company?.cif ?? ""}
                        />
                    </FormGroup>
                </div>
            </div>
        </>
    );
};

export default PickupInfo;